import { StyledProps } from '@front10';
import React, { Ref, useRef, useState } from 'react';
import { useTranslation } from '@front10/utils';
import Box from '../../elements/Box';
import { BoxProps } from '../../elements/Box/Box';
import Button from '../../elements/Button';
import { ButtonProps } from '../../elements/Button/Button';
import Header from '../../elements/Header';
import { HeaderProps } from '../../elements/Header/Header';
import Popover from '../Popover';
import BasicBudget, { BasicBudgetProps } from './Budget';
import BudgetCard, { BudgetCardProps } from './BudgetCard';

interface AdvancedBudgetProps {
  defaultBudgets?: number[];
  components?: Partial<{
    input: Partial<BasicBudgetProps>;
    popover: Partial<StyledProps<$TSFixMe>>;
    container: Partial<BoxProps>;
    headerRow: Partial<BoxProps>;
    header: Partial<HeaderProps>;
    clearButton: Partial<ButtonProps>;
    budgetCardsRow: Partial<BoxProps>;
    budgetCard: Partial<BudgetCardProps>;
    budgetInput: Partial<BasicBudgetProps>;
    applyButtonRow: Partial<BoxProps>;
    applyButton: Partial<ButtonProps>;
  }>;
}

interface ImperativeActions {
  reset: () => void;
}

function AdvancedBudget(
  {
    onChange,
    onClear,
    defaultBudgets = [50, 100, 150, 200, 250, 300],
    components = {
      budgetInput: { leftIcons: [] },
      input: { leftIcons: [] }
    },
    currencyCode
  }: BasicBudgetProps & AdvancedBudgetProps,
  ref: Ref<ImperativeActions>
) {
  const popupRef = useRef<Popover>(null);
  const budgetRef = useRef<BasicBudget>(null);
  const inputRef = useRef<BasicBudget>(null);
  const [value, setValue] = useState<number | string | undefined>(undefined);
  const translate = useTranslation();

  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);
  const [budgetValue, setBudgetValue] = useState<number | string | undefined>(undefined);

  function save(newValue?: number | '') {
    const savedValue = newValue ?? value;
    //setValue('');
    inputRef.current?.setValue(`${savedValue}`);
    setBudgetValue(savedValue);
    return savedValue;
  }

  function change(newValue?: number | '') {
    const savedValue = save(newValue);
    onChange?.(savedValue);
  }

  function open() {
    popupRef.current?.openPopover();
  }

  function close() {
    popupRef.current?.closePopover();
    inputRef.current?.inputFocus.current.focus();
  }

  function clear() {
    save('');
    setValue('');
    onClear?.();
  }

  function saveAndClose(newValue?: number | '') {
    close();
    if (newValue) {
      setValue('');
    }
    change(newValue);
  }

  function clearAllBudgets() {
    clear();
    budgetRef.current?.reset();
  }

  React.useImperativeHandle(ref, () => ({
    reset: clear
  }));

  const input = (
    <BasicBudget
      ref={inputRef}
      rightIcons={[isPopoverOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down']}
      defaultValue={value}
      autoComplete="off"
      showClear={false}
      updateDelay={0}
      label={translate('cmp-advanced-budget-input-label', 'Budget')}
      onRightIconClick={open}
      onLeftIconClick={open}
      readonly={true}
      bgColor="white"
      inputSettings={{ bgColor: 'white', cursor: 'pointer' }}
      cursor="pointer"
      rightTabIndex={[-1]}
      currencyCode={currencyCode}
      placeholderKey="cmp-advanced-budget-input-placeholder"
      {...components.input}
    />
  );

  return (
    <Popover
      element={input}
      ref={popupRef}
      minWidth="425px"
      onClose={() => setIsPopoverOpen(false)}
      onOpen={() => setIsPopoverOpen(true)}
      {...components.popover}
    >
      <Box padding="1" bgColor="white" {...components.container}>
        <Box
          display="flex"
          marginBottom="2"
          style={{ justifyContent: 'space-between', alignItems: 'baseline' }}
          {...components.headerRow}
        >
          <Header
            textAlign="left"
            tag="h6"
            fontWeight="bold"
            content={translate('cmp-advanced-budget-header', 'Budget range')}
            {...components.header}
          />
          <Button
            disabled={
              (budgetValue === undefined || budgetValue === null || budgetValue === '') &&
              (value === undefined || value === null || value === '')
            }
            bgColor="transparent"
            borderNone={true}
            onClick={clearAllBudgets}
            {...components.clearButton}
          >
            {translate('cmp-advanced-budget-clear', 'Clear')}
          </Button>
        </Box>
        <Box
          display="flex"
          style={{ flexWrap: 'wrap' }}
          marginBottom="3"
          {...components.budgetCardsRow}
        >
          {defaultBudgets.map((budget) => (
            <BudgetCard
              key={budget}
              value={budget}
              label={translate('cmp-advanced-budget-card-label', 'or less')}
              onAction={() => saveAndClose(budget)}
              {...components.budgetCard}
            />
          ))}
        </Box>
        <BasicBudget
          ref={budgetRef}
          showLabel={true}
          defaultValue={value}
          onChange={setValue}
          onClear={() => setValue(undefined)}
          updateDelay={0}
          label={translate('cmp-advanced-budget-internal-input-label', 'Set yours')}
          currencyCode={currencyCode}
          placeholderKey="cmp-advanced-budget-internal-input-placeholder"
          {...components.input}
          {...components.budgetInput}
        />
        <Box display="flex" marginBottom="3" marginTop="3" {...components.applyButtonRow}>
          <Button
            disabled={value === undefined || value === null || value === ''}
            flex="1"
            color="primary"
            paddingWidth="0"
            borderColor="white"
            onClick={() => saveAndClose()}
            {...components.applyButton}
          >
            {translate('cmp-advanced-budget-apply', 'Apply')}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
}

export default React.forwardRef(AdvancedBudget);
